import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from './ui/card';
import { Users, MessageCircle, AlertCircle, Activity, Mail, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import api from '../utils/api';

const authHeaders = () => {
  return { 'Content-Type': 'application/json' };
};

const widgets = [
  { key: 'team_size', labelKey: 'provider.opsWidgets.teamMembers', icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
  { key: 'active_threads', labelKey: 'provider.opsWidgets.activeThreads', icon: MessageCircle, color: 'text-teal-600', bg: 'bg-teal-50' },
  { key: 'unassigned_threads', labelKey: 'provider.opsWidgets.unassigned', icon: AlertCircle, color: 'text-amber-600', bg: 'bg-amber-50' },
  { key: 'activity_count_24h', labelKey: 'provider.opsWidgets.activity24h', icon: Activity, color: 'text-purple-600', bg: 'bg-purple-50' },
  { key: 'pending_invites', labelKey: 'provider.opsWidgets.pendingInvites', icon: Mail, color: 'text-rose-600', bg: 'bg-rose-50' },
];

const OperationalWidgets = ({ refreshKey, hideRefresh = false }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/dashboard-widgets`);
      setData(res.data);
    } catch { /* supplementary panel */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading && !data) return null;
  if (!data) return null;

  return (
    <div className="flex items-center gap-2" data-testid="operational-widgets">
      {widgets.map(({ key, labelKey, icon: Icon, color, bg }) => (
        <Card key={key} className="flex-1 min-w-0" data-testid={`widget-${key}`}>
          <CardContent className="px-3 py-2 flex items-center gap-2">
            <div className={`p-1.5 rounded-md ${bg} flex-shrink-0`}>
              <Icon className={`h-3.5 w-3.5 ${color}`} />
            </div>
            <div className="min-w-0">
              <p className="text-lg font-bold leading-none">{data[key] ?? '-'}</p>
              <p className="text-[10px] text-gray-400 leading-tight truncate">{t(labelKey)}</p>
            </div>
          </CardContent>
        </Card>
      ))}
      <Button
        variant="ghost"
        size="icon"
        className={`h-8 w-8 flex-shrink-0 ${hideRefresh ? 'hidden' : ''}`}
        onClick={load}
        disabled={loading}
        data-testid="refresh-widgets-btn"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
      </Button>
    </div>
  );
};

export default OperationalWidgets;
